'use client';
import React from 'react';
import Link from 'next/link';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { SingleStoreType, CustomStoreHours, Weekday } from '@workspace/interfaces/store-locator';
import { cn } from '@/app/lib/utils';
import { Icon, Typography } from '@workspace/ui/common';
import { normalizeStoreHours } from '@/app/lib/store-locator-utils';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

interface StoreCardProps {
  storeCard: SingleStoreType;
  isMapCard?: boolean;
  directionLabel: string | null | undefined;
  loading?: boolean;
  className?: string;
}

const ICON_DOT = () => <Icon name="IconDotBlack" size={8} />;

const STORE_DT_FORMAT = 'YYYY-MM-DD h:mm A';
const DISPLAY_TIME_FORMAT = 'h.mm a';

const getDayHours = (storeHours: CustomStoreHours, dayName: string) => storeHours[dayName as Weekday] ?? null;

const parseStoreTime = (dateStr: string, timeStr: string, tz?: string): dayjs.Dayjs =>
  tz ? dayjs.tz(`${dateStr} ${timeStr}`, STORE_DT_FORMAT, tz) : dayjs(`${dateStr} ${timeStr}`, STORE_DT_FORMAT);

const getNextOpenDay = (storeHours: CustomStoreHours, now: dayjs.Dayjs, storeTimezone?: string): React.ReactNode => {
  for (let i = 1; i <= 7; i++) {
    const nextDay = now.add(i, 'day');
    const nextDayName = nextDay.format('dddd').toLowerCase();
    const nextDayHours = getDayHours(storeHours, nextDayName);

    if (nextDayHours && !nextDayHours.closed && nextDayHours.openTime && nextDayHours.closeTime) {
      const openTime = parseStoreTime(nextDay.format('YYYY-MM-DD'), nextDayHours.openTime, storeTimezone).local();

      if (i === 1) {
        return (
          <>
            Open tomorrow <ICON_DOT /> {openTime.format(DISPLAY_TIME_FORMAT)}
          </>
        );
      }
      return (
        <>
          Open <ICON_DOT /> {openTime.format(DISPLAY_TIME_FORMAT)} {nextDay.format('ddd')}
        </>
      );
    }
  }
  return null;
};

const smartTimeHandler = (rawStoreHours: CustomStoreHours | undefined, storeTimezone?: string): React.ReactNode => {
  const storeHours = normalizeStoreHours(rawStoreHours);
  if (!storeHours) return '';

  const storeNow = storeTimezone ? dayjs().tz(storeTimezone) : dayjs();
  const todayName = storeNow.format('dddd').toLowerCase();
  const todayHours = getDayHours(storeHours, todayName);

  if (todayHours && !todayHours.closed) {
    const dateStr = storeNow.format('YYYY-MM-DD');
    const openTimeInStore = parseStoreTime(dateStr, todayHours.openTime ?? '', storeTimezone);
    const closeTimeInStore = parseStoreTime(dateStr, todayHours.closeTime ?? '', storeTimezone);

    if (storeNow.isAfter(openTimeInStore) && storeNow.isBefore(closeTimeInStore)) {
      return (
        <>
          Open <ICON_DOT /> Closes {closeTimeInStore.local().format(DISPLAY_TIME_FORMAT)}
        </>
      );
    }
    if (storeNow.isBefore(openTimeInStore)) {
      return (
        <>
          Open soon <ICON_DOT /> Opens {openTimeInStore.local().format(DISPLAY_TIME_FORMAT)}
        </>
      );
    }
    return getNextOpenDay(storeHours, storeNow, storeTimezone);
  }
  return getNextOpenDay(storeHours, storeNow, storeTimezone);
};

export function StoreCard({
  storeCard,
  isMapCard = false,
  loading = false,
  directionLabel,
  className,
}: StoreCardProps) {
  const time = smartTimeHandler(storeCard?.storeHours, storeCard?.timezone);

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div>
        {storeCard?.storeHours && (
          <Typography
            variant="bodyXs"
            loading={loading}
            className={cn(
              'mb-4 text-black! inline-flex items-center gap-2 whitespace-nowrap',
              isMapCard ? '' : 'lg:hidden',
            )}
          >
            {time}
          </Typography>
        )}

        <Typography
          variant="h4"
          styleVariant="h6"
          loading={loading}
          className={cn('mb-3xs md:mb-2xs text-secondary-400! line-clamp-1', {
            'mb-1! line-clamp-none': isMapCard,
          })}
        >
          {storeCard.storeName}
        </Typography>

        <Typography
          variant="bodySm"
          loading={loading}
          className={cn('mb-2 text-black! line-clamp-1', {
            'line-clamp-none': isMapCard,
          })}
        >
          {storeCard.address}
        </Typography>

        {storeCard.storeTelephone && (
          <Typography variant="bodySm" loading={loading} className="text-black!">
            T: {storeCard.storeTelephone}
          </Typography>
        )}
      </div>

      <div className={cn(isMapCard ? 'flex flex-col items-center mt-lg' : 'flex items-center justify-between mt-auto')}>
        {storeCard?.storeHours && !isMapCard && (
          <Typography
            variant="bodySm"
            loading={loading}
            className="max-lg:hidden text-black! inline-flex items-center gap-2 whitespace-nowrap"
          >
            {time}
          </Typography>
        )}

        <Link
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(storeCard.address || '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'group font-heading! text-sm lg:text-base inline-flex items-center gap-2 text-primary whitespace-nowrap ml-auto focus-visible:outline-none! focus-visible:border-none',
            {
              'animate-pulse bg-secondary-50 text-transparent rounded-md ': loading,
            },
            isMapCard ? '' : 'font-semibold!',
          )}
        >
          {directionLabel || ''}
          {!loading && (
            <Icon className="transition-all duration-300 group-hover:translate-x-1" name="IconNextRed" size={24} />
          )}
        </Link>
      </div>
    </div>
  );
}
